import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { emdFullscreenHeight } from '../emd-chart-utils';

/**
 * Expandable wholesaler grid.
 *
 * ag-grid Enterprise features used here (masterDetail, agDetailCellRenderer,
 * agGroupCellRenderer) are registered globally by WidgetModule's side-effect
 * import, which DashboardModule already pulls in — this component must stay
 * declared there for that to hold.
 */
@Component({
    selector: 'emd-hierarchical-grid',
    templateUrl: './emd-hierarchical-grid.component.html',
    styleUrls: ['./emd-hierarchical-grid.component.scss'],
    standalone: false
})
export class EmdHierarchicalGridComponent implements OnChanges, OnDestroy {
  @Input() rowData: any[] = [];
  @Input() columnDefs: any[] = [];
  @Input() childColumnDefs: any[] = [];
  @Input() grandTotal = false;
  @Input() height = 470;
  /** Cross-filter selection; rows outside it are dimmed. Empty means no cut. */
  @Input() selected: string[] = [];

  @Output() rowSelect = new EventEmitter<{ wholesaler: string; ctrlKey: boolean }>();

  grandTotalRow: any = {};

  private gridApi: any;

  constructor(private readonly host: ElementRef<HTMLElement>) {
    // Registered through Angular so the event ticks change detection, which is
    // all the height binding needs to pick up the new value.
    document.addEventListener('fullscreenchange', this.onFullscreenChange);
  }

  ngOnDestroy(): void {
    document.removeEventListener('fullscreenchange', this.onFullscreenChange);
  }

  private readonly onFullscreenChange = () => {};

  /** Design height normally; fills the screen when the card is fullscreened. */
  get effectiveHeight(): number {
    return emdFullscreenHeight(this.host.nativeElement, this.height);
  }

  /** Rows match the house grid's 50px rhythm. */
  readonly rowHeight = 50;

  /**
   * wrapHeaderText is deliberately off — the shared grid turned it on, which
   * broke the date headers across two lines. wrapText stays on so long
   * wholesaler/customer names wrap rather than truncate, as in the reference.
   */
  readonly defaultColDef = {
    resizable: true,
    sortable: true,
    filter: false,
    flex: 1,
    minWidth: 100,
    wrapHeaderText: false,
    autoHeaderHeight: false,
    unSortIcon: false,
    wrapText: true,
  };

  readonly detailCellRendererParams = {
    detailGridOptions: {
      defaultColDef: { resizable: true, sortable: false, flex: 1, minWidth: 100, wrapText: true },
      headerHeight: 32,
      // Child rows share the parent's 50px height so the expanded block reads
      // as one continuous table.
      rowHeight: 50,
      // Zebra striping on even rows, matching the reference's getRowStyle.
      getRowStyle: (params: any) =>
        params.node.rowIndex % 2 === 0 ? { background: '#FAFAFB' } : undefined,
      columnDefs: [] as any[],
    },
    getDetailRowData: (params: any) => params.successCallback(params.data.callRecords ?? []),
  };

  /** Dimming is a class rule so cross-filtering does not rebuild the grid. */
  readonly rowClassRules = {
    'emd-row-dimmed': (params: any) =>
      this.selected.length > 0 &&
      !!params.data?.wholesaler &&
      this.selected.indexOf(params.data.wholesaler) === -1,
    'emd-row-selected': (params: any) =>
      this.selected.length > 0 &&
      !!params.data?.wholesaler &&
      this.selected.indexOf(params.data.wholesaler) > -1,
  };

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['childColumnDefs']) {
      this.detailCellRendererParams.detailGridOptions.columnDefs = this.childColumnDefs;
    }

    if ((changes['rowData'] || changes['columnDefs']) && this.grandTotal) {
      this.grandTotalRow = this.calculateGrandTotal();
    }

    if (changes['selected'] && this.gridApi) {
      this.gridApi.redrawRows();
    }
  }

  onGridReady(event: any): void {
    this.gridApi = event.api;
  }

  onQuickFilterChanged(term: string): void {
    this.gridApi?.setQuickFilter(term ?? '');
  }

  /** CSV of the parent grid as displayed; driven by the card header's export menu. */
  exportCsv(fileName = 'export'): void {
    this.gridApi?.exportDataAsCsv({ fileName: `${fileName}.csv` });
  }

  onRowClicked(event: any): void {
    const name = event?.data?.wholesaler;
    if (!name) { return; }
    const source = event.event as MouseEvent | undefined;
    this.rowSelect.emit({
      wholesaler: name,
      ctrlKey: !!(source?.ctrlKey || source?.metaKey),
    });
  }

  /**
   * Pinned bottom row. Only the two aggFuncs these grids actually declare are
   * supported: `sum`, and the literal 'Grand Total' label column.
   */
  private calculateGrandTotal(): any {
    const total: any = {};

    for (const col of this.columnDefs) {
      const field = col.field;
      if (!field) { continue; }

      if (col.aggFunc === 'Grand Total') {
        total[field] = 'Total';
      } else if (col.aggFunc === 'sum') {
        total[field] = this.rowData.reduce((sum, row) => sum + (+row[field] || 0), 0);
      }
    }

    return total;
  }
}
